/**
 * Student courses landing : tabs (Tronc commun / Modules complémentaires),
 * each showing themes that have at least 1 published chapter.
 * Route: /portal/courses
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Folder } from "@phosphor-icons/react";
import { coursesApi } from "../../api/courses";
import { TRACKS } from "../../lib/tracks";
import PortalPageHeader from "../../components/portal/PortalPageHeader";
import PortalEmptyState from "../../components/portal/PortalEmptyState";

export default function PortalCourses() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tracks, setTracks] = useState([]);
  const [activeTrack, setActiveTrack] = useState(
    searchParams.get("track") || TRACKS[0].slug,
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await coursesApi.tracks();
      // For students, hide themes that have zero published chapter
      setTracks(
        data.map((tr) => ({
          ...tr,
          themes: tr.themes.filter((th) => th.chapter_count > 0),
        })),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeThemes = useMemo(
    () => tracks.find((t2) => t2.slug === activeTrack)?.themes || [],
    [tracks, activeTrack],
  );

  const setTab = useCallback(
    (slug) => {
      setActiveTrack(slug);
      setSearchParams({ track: slug });
    },
    [setSearchParams],
  );

  return (
    <div data-testid="portal-courses-page" className="space-y-10">
      <PortalPageHeader
        kickerKey="portal.placeholder.kicker"
        titleKey="portal.nav.courses"
        subtitleKey="portal.pages.courses_desc"
      />

      {/* Tabs */}
      <div
        data-testid="portal-courses-tabs"
        className="flex items-center gap-1 border-b border-slate-200"
      >
        {TRACKS.map((tr) => (
          <button
            type="button"
            key={tr.slug}
            onClick={() => setTab(tr.slug)}
            data-testid={`portal-courses-tab-${tr.slug}`}
            className={`relative px-4 py-3 text-sm font-medium transition-colors ${
              activeTrack === tr.slug
                ? "text-slate-900"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            {t(tr.labelKey)}
            {activeTrack === tr.slug && (
              <span className="absolute inset-x-3 -bottom-px h-0.5 bg-slate-900" />
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : activeThemes.length === 0 ? (
        <PortalEmptyState
          variant="dashed"
          icon={Folder}
          titleKey="portal.courses.empty_title"
          descKey="portal.courses.empty_desc"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeThemes.map((th) => (
            <Link
              key={th.id}
              to={`/portal/courses/${activeTrack}/${th.slug}`}
              data-testid={`portal-theme-${th.slug}`}
              className="group block rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-slate-900 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl adonis-gradient text-white">
                <Folder size={20} weight="regular" />
              </span>
              <h3 className="mt-5 font-heading text-base md:text-lg font-bold tracking-tight text-slate-900">
                {th.title}
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {th.chapter_count} {t("portal.courses.chapters")}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-slate-700 group-hover:translate-x-1 transition-transform">
                {t("portal.courses.open")}
                <ArrowRight size={14} weight="bold" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
